import { Component, ChangeDetectionStrategy } from '@angular/core';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { ColumnDefinitionService } from '../../../rent/service/data/column-definition.service';
import { FilterDefinitionItem, FilterDefinitionItemLocalDate } from '../../../core/model/filter-definition-item';
import {
  BankTransactionImport,
  BankTransactionStatus,
  BankTransactionStatusToString,
} from '../../../invoice/model/bank-transaction-import';
import { BankTransactionImportService } from '../../service/data/bank-transaction-import.service';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { IncorrectPaymentCommand } from '../../command/incorrect-payment';
import { standardCommand } from '../../../core/model/command';
import { BankTransactionStatusCommand } from '../../command/bankTransactionStatusCommand';
import { BankImportTransactionCommandHandlerResolver } from '../../service/command/bank-import-transaction-command-handler-resolver';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { ActivatedRoute } from '@angular/router';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
    selector: 'app-bank-transaction-import-manager',
    templateUrl: './../../../core/component/paged-object-manager/paged-object-manager.html',
    styleUrls: ['./../../../core/component/paged-object-manager/paged-object-manager.scss'],
    providers: [BankImportTransactionCommandHandlerResolver],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UnhandledBankTransactionImportManagerComponent extends PagedObjectManagerComponent<BankTransactionImport> {
  private canManageBankTransactionImports = false;

  constructor(
    private _dialogService: ActionWindowService,
    columnDefinitionService: ColumnDefinitionService,
    private _dataService: BankTransactionImportService,
    private _companyService: CompanyService,
    dispatcher: CommandDispatcher,
    commandHandlerResolver: BankImportTransactionCommandHandlerResolver,
    messageService: MessageService,
    navigationService: NavigationService,
    accountService: UserAccountService,
    activatedRoute?: ActivatedRoute
  ) {
    super(_dataService, messageService, navigationService, dispatcher, commandHandlerResolver);
    this.columnDefinition = columnDefinitionService.getBankTransactionImportColumnDefinition();
    this.contextMenuDefinition = [
      new MenuItemDefinition('EDIT_BTN', standardCommand.edit),
    ];
    this.menuDefinition = [];

    this.title = 'BANK_IMPORT_TRANSACTIONS.BANK_IMPORT_TRANSACTIONS';
    this.allowNew = false;
    this.helpText = 'BANK_IMPORT_TRANSACTIONS.BANK_TRANSACTION_INFO';
    this.AddSubscription(
      accountService.current.subscribe(account => {
        if (this.canManageBankTransactionImports || !account.hasAccess(ModuleNames.Invoicing, Features.ManageIncorrectPayments)) {
          return;
        }

        this.canManageBankTransactionImports = true;
        this.contextMenuDefinition.unshift(
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.IGNORE', BankTransactionStatusCommand.setStatusIngored),
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.UNDO_IGNORED', BankTransactionStatusCommand.setStatusUnhandled),
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.BOOK_AS_INCORRECT_PAYMENT', BankTransactionStatusCommand.setStatusHandled)
        );
        this.menuDefinition.push(
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.IGNORE', BankTransactionStatusCommand.setStatusIngored),
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.UNDO_IGNORED', BankTransactionStatusCommand.setStatusUnhandled),
          new MenuItemDefinition('BANK_IMPORT_TRANSACTIONS.BOOK_AS_INCORRECT_PAYMENT', BankTransactionStatusCommand.setStatusHandled)
        );
      })
    );

    if (activatedRoute) {
      this.AddSubscription(
        activatedRoute.queryParamMap.subscribe(map => {
          if (map.has('transactionStatus')) {
            const param = map.get('transactionStatus');
            if (param) {
              const status = BankTransactionStatus[param[0].toUpperCase() + param.substring(1)];
              if (status) {
                this._transactionStatus = status;
              }
            }
          }
        })
      );
    }
    this.preferencesKey = 'invoice.bank-transaction-import';
  }

  private _transactionStatus: BankTransactionStatus;

  cancel() {
    this._dialogService.close();
  }

  ngOnInit() {
    this.AddSubscription(
      this._dataService.getFilterDefinition().subscribe({
        next: definition => {
          const overviewQueryExists = this._transactionStatus;
          this.defaultFilters = definition.filter.defaultFields;

          this.filterDefinition = [
            new FilterDefinitionItem(
              'COMMON.COMPANY',
              'companyId',
              overviewQueryExists ? null : definition.filter.companyId,
              this._companyService,
              definition.filter.selectedFields.includes('companyId')
            ),
            new FilterDefinitionItem(
              'COMPANY.BANK_ACCOUNT.IBAN',
              'bankAccountNumber',
              overviewQueryExists ? null : definition.filter.bankAccountNumber,
              null,
              definition.filter.selectedFields.includes('bankAccountNumber')
            ),
            new FilterDefinitionItem(
              'BANK_IMPORT_TRANSACTIONS.DEBTORNAME',
              'debtorName',
              overviewQueryExists ? '' : definition.filter.debtorName,
              null,
              definition.filter.selectedFields.includes('debtorName')
            ),
            new FilterDefinitionItem(
              'BANK_IMPORT_TRANSACTIONS.TRANSACTION_TEXT',
              'transactionText',
              overviewQueryExists ? '' : definition.filter.transactionText,
              null,
              definition.filter.selectedFields.includes('transactionText')
            ),
            new FilterDefinitionItemLocalDate(
              'BANK_IMPORT_TRANSACTIONS.IMPORTED_AT_FROM',
              'importedAtFrom',
              overviewQueryExists ? null : definition.filter.importedAtFrom,
              definition.filter.selectedFields.includes('importedAtFrom')
            ),
            new FilterDefinitionItemLocalDate(
              'BANK_IMPORT_TRANSACTIONS.IMPORTED_AT_TO',
              'importedAtTo',
              overviewQueryExists ? null : definition.filter.importedAtTo,
              definition.filter.selectedFields.includes('importedAtTo')
            ),
            new FilterDefinitionItemLocalDate(
              'BANK_IMPORT_TRANSACTIONS.TRANSACTION_DATE_FROM',
              'transactionDateFrom',
              overviewQueryExists ? null : definition.filter.transactionDateFrom,
              definition.filter.selectedFields.includes('transactionDateFrom')
            ),
            new FilterDefinitionItemLocalDate(
              'BANK_IMPORT_TRANSACTIONS.TRANSACTION_DATE_TO',
              'transactionDateTo',
              overviewQueryExists ? null : definition.filter.transactionDateTo,
              definition.filter.selectedFields.includes('transactionDateTo')
            ),
            new FilterDefinitionItem(
              'BANK_IMPORT_TRANSACTIONS.TRANSACTION_STATUS',
              'transactionStatus',
              this._transactionStatus ? this._transactionStatus : definition.filter.transactionStatus,
              [
                {
                  caption: 'ALL_TEXT',
                  value: '',
                },
                {
                  caption: BankTransactionStatusToString(BankTransactionStatus.Unhandled),
                  value: BankTransactionStatus.Unhandled,
                },
                {
                  caption: BankTransactionStatusToString(BankTransactionStatus.Handled),
                  value: BankTransactionStatus.Handled,
                },
                {
                  caption: BankTransactionStatusToString(BankTransactionStatus.Ignored),
                  value: BankTransactionStatus.Ignored,
                },
              ],
              this._transactionStatus ? true : definition.filter.selectedFields.includes('transactionStatus')
            ),
          ];
        },
        error: error => {
          this.messageService.showError(error);
        },
      })
    );
  }
}
